import { FC, ReactNode } from 'react'
import cn from 'classnames'
import type { Editor } from '@tiptap/react'
import {
  BoldOutlined,
  DeleteOutlined,
  FileOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlusOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { iconProps } from './TaskTrackerRichTextEditor.shared'
import classes from './index.module.css'

type ToolbarButtonProps = {
  active?: boolean
  disabled?: boolean
  title: string
  onAction: () => void
  children: ReactNode
  className?: string
}

const ToolbarButton: FC<ToolbarButtonProps> = ({
  active = false,
  disabled = false,
  title,
  onAction,
  children,
  className,
}) => (
  <button
    type="button"
    className={cn(
      classes.toolbarButton,
      {
        [classes.toolbarButtonActive]: active,
      },
      className,
    )}
    title={title}
    aria-label={title}
    disabled={disabled}
    onMouseDown={(event) => {
      event.preventDefault()
      if (disabled) return
      onAction()
    }}
  >
    {children}
  </button>
)

type TaskTrackerRichTextEditorToolbarProps = {
  editor: Editor
  onSetLink: () => void
  onPickImageFile: () => void
  onPickAttachmentFile: () => void
  allowAttachments?: boolean
  onInsertTable: () => void
  onAddTableRow: () => void
  onAddTableColumn: () => void
  onDeleteTableRow: () => void
  onDeleteTableColumn: () => void
  onDeleteTable: () => void
}

const TaskTrackerRichTextEditorToolbar: FC<TaskTrackerRichTextEditorToolbarProps> = ({
  editor,
  onSetLink,
  onPickImageFile,
  onPickAttachmentFile,
  allowAttachments = true,
  onInsertTable,
  onAddTableRow,
  onAddTableColumn,
  onDeleteTableRow,
  onDeleteTableColumn,
  onDeleteTable,
}) => (
  <div className={classes.toolbar}>
    <div className={classes.toolbarGroup}>
      <ToolbarButton
        title="Жирный"
        active={editor.isActive('bold')}
        onAction={() => editor.chain().focus().extendMarkRange('bold').toggleBold().run()}
      >
        <BoldOutlined {...iconProps} />
      </ToolbarButton>
      <ToolbarButton
        title="Курсив"
        active={editor.isActive('italic')}
        onAction={() => editor.chain().focus().extendMarkRange('italic').toggleItalic().run()}
      >
        <ItalicOutlined {...iconProps} />
      </ToolbarButton>
      <ToolbarButton
        title="Подчёркнутый"
        active={editor.isActive('underline')}
        onAction={() =>
          editor.chain().focus().extendMarkRange('underline').toggleUnderline().run()
        }
      >
        <UnderlineOutlined {...iconProps} />
      </ToolbarButton>
      <ToolbarButton
        title="Зачёркнутый"
        active={editor.isActive('strike')}
        onAction={() => editor.chain().focus().extendMarkRange('strike').toggleStrike().run()}
      >
        <StrikethroughOutlined {...iconProps} />
      </ToolbarButton>
    </div>

    <div className={classes.toolbarGroup}>
      <ToolbarButton
        title="Маркированный список"
        active={editor.isActive('bulletList')}
        onAction={() => editor.chain().focus().toggleBulletList().run()}
      >
        <UnorderedListOutlined {...iconProps} />
      </ToolbarButton>
      <ToolbarButton
        title="Нумерованный список"
        active={editor.isActive('orderedList')}
        onAction={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <OrderedListOutlined {...iconProps} />
      </ToolbarButton>
    </div>

    <div className={classes.toolbarGroup}>
      <ToolbarButton
        title="Ссылка"
        active={editor.isActive('link')}
        onAction={onSetLink}
      >
        <LinkOutlined {...iconProps} />
      </ToolbarButton>
      {allowAttachments ? (
        <>
          <ToolbarButton title="Вставить изображение" onAction={onPickImageFile}>
            <PictureOutlined {...iconProps} />
          </ToolbarButton>
          <ToolbarButton title="Прикрепить файл" onAction={onPickAttachmentFile}>
            <span className={classes.toolbarAttachmentIconWrap}>
              <PaperClipOutlined {...iconProps} />
              <FileOutlined {...iconProps} />
            </span>
          </ToolbarButton>
        </>
      ) : null}
    </div>

    <div className={classes.toolbarGroup}>
      <ToolbarButton title="Вставить таблицу" onAction={onInsertTable}>
        <TableOutlined {...iconProps} />
      </ToolbarButton>
      <ToolbarButton
        title="Добавить строку"
        disabled={!editor.isActive('table')}
        className={classes.toolbarButtonLabeled}
        onAction={onAddTableRow}
      >
        <span className={classes.toolbarLabeledControl}>
          <PlusOutlined {...iconProps} />
          <span className={classes.toolbarControlLabel}>стр</span>
        </span>
      </ToolbarButton>
      <ToolbarButton
        title="Добавить колонку"
        disabled={!editor.isActive('table')}
        className={classes.toolbarButtonLabeled}
        onAction={onAddTableColumn}
      >
        <span className={classes.toolbarLabeledControl}>
          <PlusOutlined {...iconProps} />
          <span className={classes.toolbarControlLabel}>кол</span>
        </span>
      </ToolbarButton>
      <ToolbarButton
        title="Удалить строку"
        disabled={!editor.isActive('table')}
        className={classes.toolbarButtonLabeled}
        onAction={onDeleteTableRow}
      >
        <span className={classes.toolbarLabeledControl}>
          <DeleteOutlined {...iconProps} />
          <span className={classes.toolbarControlLabel}>стр</span>
        </span>
      </ToolbarButton>
      <ToolbarButton
        title="Удалить колонку"
        disabled={!editor.isActive('table')}
        className={classes.toolbarButtonLabeled}
        onAction={onDeleteTableColumn}
      >
        <span className={classes.toolbarLabeledControl}>
          <DeleteOutlined {...iconProps} />
          <span className={classes.toolbarControlLabel}>кол</span>
        </span>
      </ToolbarButton>
      <ToolbarButton
        title="Удалить таблицу"
        disabled={!editor.isActive('table')}
        onAction={onDeleteTable}
      >
        <DeleteOutlined {...iconProps} />
      </ToolbarButton>
    </div>
  </div>
)

export default TaskTrackerRichTextEditorToolbar

